import React, { Component } from 'react';
import { connect } from 'react-redux';
import ScrollArea from 'react-scrollbar';
import Spinner from 'material-ui/CircularProgress';

import Defaults from 'superagent-defaults';

import TextField from '../../components/basic/TextField';
import Button from '../../components/basic/Button';
import ModelPicker from '../../components/basic/ModelPicker';
import { Section, SectionItem } from '../../components/basic/section';
import { DEFAULT_LLM_MODEL } from '../../constants/Constants';

// Helper for local API calls
const API_URL = ''; // Same as in Api.js
function authRequest(token) {
  const superagent = new Defaults();
  if (token) {
    superagent.set('Authorization', `Bearer ${token}`);
  }
  return superagent;
}

const fetchEchoPulseMainPrompt = token =>
  authRequest(token).get(`${API_URL}/api/v1/admin/ai/prompts/echo-pulse-result-chat/main`);

const updateEchoPulseMainPrompt = (data, token) =>
  authRequest(token).put(`${API_URL}/api/v1/admin/ai/prompts/echo-pulse-result-chat/main`).send(data);

const fetchEchoPulseFollowUpPrompt = token =>
  authRequest(token).get(`${API_URL}/api/v1/admin/ai/prompts/echo-pulse-result-chat/follow-up`);

const updateEchoPulseFollowUpPrompt = (data, token) =>
  authRequest(token).put(`${API_URL}/api/v1/admin/ai/prompts/echo-pulse-result-chat/follow-up`).send(data);

class EchoPulseResultChatPromptContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      activeLanguage: 'en', // 'EN' or 'CS'
      mainPrompt: {
        modelParams: {
          temperature: 0,
          maxTokens: 1000,
          model: DEFAULT_LLM_MODEL.value,
          location: null
        },
        languages: {
          en: { systemInstruction: '', firstMessageTemplate: '', frictionPointQueryTemplate: '' },
          cs: { systemInstruction: '', firstMessageTemplate: '', frictionPointQueryTemplate: '' }
        }
      },
      followUpPrompt: {
        modelParams: {
          temperature: 0.2,
          maxTokens: 500,
          model: DEFAULT_LLM_MODEL.value,
          location: null
        },
        languages: {
          en: { systemInstruction: '', promptTemplate: '' },
          cs: { systemInstruction: '', promptTemplate: '' }
        }
      },
      error: null,
      successMessage: null
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    const { auth } = this.props;
    try {
      this.setState({ loading: true, error: null });
      const [mainRes, followUpRes] = await Promise.all([
        fetchEchoPulseMainPrompt(auth.access_token),
        fetchEchoPulseFollowUpPrompt(auth.access_token)
      ]);

      this.setState({
        mainPrompt: mainRes.body,
        followUpPrompt: followUpRes.body,
        loading: false
      });
    } catch (err) {
      console.error('Failed to fetch prompts', err);
      this.setState({ loading: false, error: 'Failed to load prompts.' });
    }
  };

  handleMainParamChange = (key, value) => {
    this.setState(prevState => ({
      mainPrompt: {
        ...prevState.mainPrompt,
        modelParams: {
          ...prevState.mainPrompt.modelParams,
          [key]: value
        }
      }
    }));
  };

  handleFollowUpParamChange = (key, value) => {
    this.setState(prevState => ({
      followUpPrompt: {
        ...prevState.followUpPrompt,
        modelParams: {
          ...prevState.followUpPrompt.modelParams,
          [key]: value
        }
      }
    }));
  };

  handleMainLangChange = (field, value) => {
    const { activeLanguage } = this.state;
    this.setState(prevState => ({
      mainPrompt: {
        ...prevState.mainPrompt,
        languages: {
          ...prevState.mainPrompt.languages,
          [activeLanguage]: {
            ...prevState.mainPrompt.languages[activeLanguage],
            [field]: value
          }
        }
      }
    }));
  };

  handleFollowUpLangChange = (field, value) => {
    const { activeLanguage } = this.state;
    this.setState(prevState => ({
      followUpPrompt: {
        ...prevState.followUpPrompt,
        languages: {
          ...prevState.followUpPrompt.languages,
          [activeLanguage]: {
            ...prevState.followUpPrompt.languages[activeLanguage],
            [field]: value
          }
        }
      }
    }));
  };

  handleSave = async () => {
    const { auth } = this.props;
    const { mainPrompt, followUpPrompt } = this.state;

    this.setState({ saving: true, error: null, successMessage: null });

    try {
      await Promise.all([
        updateEchoPulseMainPrompt(mainPrompt, auth.access_token),
        updateEchoPulseFollowUpPrompt(followUpPrompt, auth.access_token)
      ]);
      this.setState({ saving: false, successMessage: 'Prompts saved successfully!' });

      // Clear success message after 3 seconds
      setTimeout(() => {
        this.setState({ successMessage: null });
      }, 3000);
    } catch (err) {
      console.error('Failed to save prompts', err);
      this.setState({ saving: false, error: 'Failed to save prompts.' });
    }
  };

  renderModelParams = (params, onChange, titlePrefix) => (
    <div className="row">
      <div className="col-sm-3">
        <ModelPicker
          floatingLabelText={`${titlePrefix} Model`}
          value={params.model}
          onChange={val => onChange('model', val)}
          fullWidth
        />
      </div>
      <div className="col-sm-3">
        <TextField
          label={`${titlePrefix} Temperature`}
          value={params.temperature}
          onChange={e => onChange('temperature', parseFloat(e.target.value))}
          type="number"
          step={0.1}
          min={0}
          max={2}
          fullWidth
        />
      </div>
      <div className="col-sm-3">
        <TextField
          label={`${titlePrefix} Max Tokens`}
          value={params.maxTokens}
          onChange={e => onChange('maxTokens', parseInt(e.target.value, 10))}
          type="number"
          fullWidth
          min={1}
          max={65535}
        />
      </div>
      <div className="col-sm-3">
        <TextField
          label={`${titlePrefix} Location`}
          value={params.location || ''}
          onChange={e => onChange('location', e.target.value.trim() === '' ? null : e.target.value)}
          fullWidth
        />
      </div>
    </div>
  );

  render() {
    const { loading, saving, activeLanguage, mainPrompt, followUpPrompt, error, successMessage } = this.state;

    if (loading) {
      return (
        <div className="flex flex-justify-center flex-align-center w-full">
          <Spinner />
        </div>
      );
    }

    const activeMainLang = mainPrompt.languages[activeLanguage] || {};
    const activeFollowUpLang = followUpPrompt.languages[activeLanguage] || {};

    return (
      <div className="Form">
        <ScrollArea>
          <div className="Form-content-container">
            <div className="container">
              <div className="row">
                <div className="col-md-12">
                  {error && (
                    <div className="Text-error" style={{ marginBottom: 20 }}>
                      {error}
                    </div>
                  )}
                  {successMessage && (
                    <div className="Text-success" style={{ marginBottom: 20, color: 'green' }}>
                      {successMessage}
                    </div>
                  )}

                  <Section title="Language">
                    <SectionItem>
                      <Button
                        label="English (EN)"
                        primary={activeLanguage === 'en'}
                        onClick={() => this.setState({ activeLanguage: 'en' })}
                        style={{ marginRight: 10 }}
                      />
                      <Button
                        label="Czech (CS)"
                        primary={activeLanguage === 'cs'}
                        onClick={() => this.setState({ activeLanguage: 'cs' })}
                      />
                    </SectionItem>
                  </Section>

                  <Section title="Main Prompt (Evaluator)">
                    <SectionItem>
                      {this.renderModelParams(mainPrompt.modelParams, this.handleMainParamChange, 'Main')}
                    </SectionItem>
                    <SectionItem>
                      <TextField
                        label={`System Instruction (${activeLanguage})`}
                        value={activeMainLang.systemInstruction || ''}
                        onChange={e => this.handleMainLangChange('systemInstruction', e.target.value)}
                        multiLine
                        rows={4}
                        rowsMax={10}
                        fullWidth
                        inputStyle={{ fontFamily: 'monospace' }}
                        helperText="You can reference `companyPromptContext` variable"
                      />
                    </SectionItem>
                    <SectionItem>
                      <TextField
                        label={`First Message Template (${activeLanguage})`}
                        value={activeMainLang.firstMessageTemplate || ''}
                        onChange={e => this.handleMainLangChange('firstMessageTemplate', e.target.value)}
                        multiLine
                        rows={4}
                        rowsMax={10}
                        fullWidth
                        inputStyle={{ fontFamily: 'monospace' }}
                        helperText="You can reference `model` (the same way as in the echo pulse prompt template), `resultDataJson`, `companyPromptContext` and `userQuery` variables"
                      />
                    </SectionItem>
                    <SectionItem>
                      <TextField
                        label={`Friction Point Query Template (${activeLanguage})`}
                        value={activeMainLang.frictionPointQueryTemplate || ''}
                        onChange={e => this.handleMainLangChange('frictionPointQueryTemplate', e.target.value)}
                        multiLine
                        rows={2}
                        rowsMax={6}
                        fullWidth
                        inputStyle={{ fontFamily: 'monospace' }}
                        helperText="You can reference `frictionPointTitle` variable"
                      />
                    </SectionItem>
                  </Section>

                  <Section title="Follow-up Prompt (Suggester)">
                    <SectionItem>
                      {this.renderModelParams(followUpPrompt.modelParams, this.handleFollowUpParamChange, 'Follow-up')}
                    </SectionItem>
                    <SectionItem>
                      <TextField
                        label={`System Instruction (${activeLanguage})`}
                        value={activeFollowUpLang.systemInstruction || ''}
                        onChange={e => this.handleFollowUpLangChange('systemInstruction', e.target.value)}
                        multiLine
                        rows={4}
                        rowsMax={10}
                        fullWidth
                        inputStyle={{ fontFamily: 'monospace' }}
                      />
                    </SectionItem>
                    <SectionItem>
                      <TextField
                        label={`Prompt Template (${activeLanguage})`}
                        value={activeFollowUpLang.promptTemplate || ''}
                        onChange={e => this.handleFollowUpLangChange('promptTemplate', e.target.value)}
                        multiLine
                        rows={2}
                        rowsMax={6}
                        fullWidth
                        inputStyle={{ fontFamily: 'monospace' }}
                      />
                    </SectionItem>
                  </Section>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
        <div className="Form-buttons-container">
          <div className="Form-buttons">
            <Button
              label={saving ? 'Saving...' : 'Save All'}
              primary
              raised
              onClick={this.handleSave}
              disabled={saving || loading}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(EchoPulseResultChatPromptContainer);



// WEBPACK FOOTER //
// ./src/containers/private/EchoPulseResultChatPromptContainer.js